import { getCommunityDetailQueryKey } from '@pba/react-network';
import { useQueryClient } from '@tanstack/react-query';
import { useMutation } from 'query';
import { handleAxiosError } from 'ui';
import type { ApiClientResponseError, ResponseErrorDataObject } from 'common/services';
import { type CommunityInvitationChangeRequest, changeRequest } from 'features/change-requests';
import { getUsersFeatureQueryKey } from 'features/users';
import { getCommunityMembersQueriesKey } from '../keys';

const defaultMutateOnError = (error: ApiClientResponseError<ResponseErrorDataObject>) => {
  const errorMessage = error.response?.status === 422 ? 'errorUserAlreadyInvited' : 'Failed to invite users.';
  handleAxiosError(error, errorMessage);
};

/**
 * @deprecated Move this mutation into the change-requests feature. It belongs next to `changeRequest`, not under communities.
 */
export const useInviteUsersMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, { id: string; invitees: { emails: string[]; phones: string[]; userIds: string[] } }>({
    defaultMutateOnError,
    async mutationFn({ id, invitees }) {
      const changeRequests: CommunityInvitationChangeRequest[] = [];

      for (const email of invitees.emails) {
        changeRequests.push({
          change_request: {
            kind: 'community_invitation',
            meta: { invitation_email: email },
            subject_id: id,
            subject_type: 'Community',
          },
        });
      }

      for (const phone of invitees.phones) {
        changeRequests.push({
          change_request: {
            kind: 'community_invitation',
            meta: { invitation_phone: phone },
            subject_id: id,
            subject_type: 'Community',
          },
        });
      }

      for (const userId of invitees.userIds) {
        changeRequests.push({
          change_request: {
            kind: 'community_invitation',
            subject_id: id,
            subject_type: 'Community',
            subject_user_id: userId,
          },
        });
      }

      const response = await changeRequest({ body: changeRequests });
      return response.data;
    },
    async onSettled(_data, _error, { id }) {
      await queryClient.invalidateQueries({ queryKey: getCommunityDetailQueryKey({ id }) });
      await queryClient.invalidateQueries({ queryKey: getCommunityMembersQueriesKey() });
      await queryClient.invalidateQueries({ queryKey: getUsersFeatureQueryKey() });
    },
  });
};
